import { message, type UploadFile } from 'antd'
import { postJSON } from 'common/services/api'
import { useMutation, useQueryClient } from 'react-query'
import { uploadDocuments } from '../../../../../common/utils/documentUpload'
import { SERVICE_RECORDS_CATEGORY_ID } from '../constants'
import type { ServiceRecord, UpdateServiceRecordInput } from '../types'

interface UseServiceRecordUpdateReturn {
  updateServiceRecord: (
    input: UpdateServiceRecordInput,
    fileList?: UploadFile[]
  ) => Promise<ServiceRecord>
  isUpdating: boolean
}

export const useServiceRecordUpdate = (
  residentId: string,
  onSuccess?: () => void
): UseServiceRecordUpdateReturn => {
  const queryClient = useQueryClient()

  const mutation = useMutation(
    async ({
      input,
      fileList
    }: {
      input: UpdateServiceRecordInput
      fileList?: UploadFile[]
    }) => {
      const { id, existingDocumentIds, ...updateData } = input

      const hasFiles = fileList && fileList.length > 0
      let documentIds: string[] | undefined

      if (hasFiles) {
        try {
          const existingIds = existingDocumentIds || []
          const newDocumentIds = await uploadDocuments(
            fileList,
            residentId,
            SERVICE_RECORDS_CATEGORY_ID
          )
          documentIds = [...existingIds, ...newDocumentIds]
        } catch (error) {
          throw new Error('Failed to upload documents', { cause: error })
        }
      }

      const response = await postJSON({
        url: `/v6/residents/service-records/${id}`,
        method: 'PATCH',
        json: {
          ...updateData,
          ...(documentIds !== undefined && { documents: documentIds })
        }
      })

      return response.data as ServiceRecord
    },
    {
      onSuccess: () => {
        message.success('Service record updated successfully')
        queryClient.invalidateQueries(['service-records', residentId])
        if (onSuccess) {
          onSuccess()
        }
      },
      onError: (error: any) => {
        message.error(error?.message || 'Failed to update service record')
      }
    }
  )

  const updateServiceRecord = async (
    input: UpdateServiceRecordInput,
    fileList?: UploadFile[]
  ): Promise<ServiceRecord> => {
    return mutation.mutateAsync({ input, fileList })
  }

  return {
    updateServiceRecord,
    isUpdating: mutation.isLoading
  }
}
